import {
  useQuery,
  UseQueryOptions,
  useSuspenseQuery,
  UseSuspenseQueryOptions
} from "@tanstack/react-query";

import api from "../api";
import { sampleReportKeys } from "../queryKeys";

interface ExtractedScreenshotsQueryOptions {
  hash: string;
  analysisId: string;
  isNetwork: boolean;
}
interface ScreenshotData {
  screenshots: string[];
  groups: [
    {
      length: number;
      type: string;
      sha1: string;
      url: string;
    }
  ];
}

const extractedScreenshotsQueryOptions = ({
  hash,
  analysisId,
  isNetwork
}: ExtractedScreenshotsQueryOptions): UseQueryOptions => ({
  queryKey: sampleReportKeys.fetchExtractedScreenshots(
    hash,
    analysisId,
    isNetwork
  ),
  queryFn: () =>
    api.downloadRLSandboxExtractedScreenshots(hash, analysisId, isNetwork),
  select: ({ data }) => ({
    screenshots: data.screenshots,
    groups: data.groups
  }),
  refetchOnMount: false,
  retry: false
});
/**
 * Downloads extracted RL Cloud Sandbox screenshots paths
 * @param hash samples hash value
 * @param analysisId analysisId for specific analysis
 * @param isNetwork fetch network report
 * @param options useQueryOptions
 */
export const useFetchExtractedScreenshots = (
  hash: string,
  analysisId: string,
  isNetwork = false,
  options?: Partial<UseQueryOptions>
) => {
  const { data, isError, error, isLoading, refetch } = useQuery({
    ...extractedScreenshotsQueryOptions({ hash, analysisId, isNetwork }),
    ...options
  });
  const screenshotData = data as ScreenshotData;
  return {
    imagePaths: (screenshotData?.screenshots || []) as string[],
    imageGroups: screenshotData?.groups || [],
    error,
    isError,
    isInitialLoading: isLoading,
    refetch
  };
};

export const useFetchExtractedScreenshotsSuspense = (
  hash: string,
  analysisId: string,
  isNetwork = false,
  options?: Partial<UseSuspenseQueryOptions>
) => {
  const { data, error, isLoading } = useSuspenseQuery({
    ...extractedScreenshotsQueryOptions({ hash, analysisId, isNetwork }),
    ...options
  });
  const screenshotData = data as ScreenshotData;
  return {
    imagePaths: (screenshotData.screenshots || []) as string[],
    imageGroups: screenshotData.groups || [],
    error,
    isInitialLoading: isLoading
  };
};
